import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {MatSelectChange} from '@angular/material/select';
import {ReplaySubject} from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil
} from 'rxjs/operators';

import {DobryAgentCategory} from '../../interface/dobry-agent';
import {DobryAgentCategoryService} from '../../services/dobry-agent-category.service';
import {ListResponse} from '../../interface/response/list-response';

@Component({
  selector: 'app-dict-dobry-agent-categories',
  templateUrl: './dict-dobry-agent-categories.component.html',
  styleUrls: ['./dict-dobry-agent-categories.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DictDobryAgentCategoriesComponent)
    }
  ]
})
export class DictDobryAgentCategoriesComponent
  implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label = 'Kategoria ubezpieczenia';
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() onlyActive = true;

  @Output()
  selectedCategory: EventEmitter<DobryAgentCategory>
    = new EventEmitter<DobryAgentCategory>();

  @Output()
  selectedCategories: EventEmitter<DobryAgentCategory[]>
    = new EventEmitter<DobryAgentCategory[]>();

  externalControl = true;
  disabled = false;

  allItems: DobryAgentCategory[] = [];
  items: DobryAgentCategory[] = [];

  value?: string | string[] = null;

  categoryFilterCtrl: FormControl;

  private destroyed$: ReplaySubject<boolean>
    = new ReplaySubject<boolean>(1);

  constructor(
    private categoryService: DobryAgentCategoryService,
    private fb: FormBuilder,
  ) {
    this.categoryFilterCtrl = this.fb.control('');
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl(this.multiple ? [] : '');
      this.externalControl = false;
    }

    this.categoryService
      .getDobryAgentCategoryNotCountedListSubject()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: ListResponse<DobryAgentCategory>) => {
        this.allItems = response.items ?? [];
        this.items = [...this.allItems];

        this.emitSelectedItems();
      });

    this.categoryFilterCtrl.valueChanges
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((value: string | null) => {
        this.filterItems(value);
      });

    this.loadDefaultList();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: string | string[] | null): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(event: MatSelectChange): void {
    this.value = event.value;
    this.onChange(this.value);

    this.emitSelectedItems();
  }

  clearValue(): void {
    const value = this.multiple ? [] : '';

    this.control.setValue(value);
    this.writeValue(value);
    this.onChange(value);

    if (this.multiple) {
      this.selectedCategories.emit([]);
    }
  }

  private loadDefaultList(): void {
    this.categoryService.loadNotCountedList(
      this.onlyActive
        ? {active: true}
        : {}
    );
  }

  private filterItems(value: string | null): void {
    const search = (value ?? '')
      .trim()
      .toLocaleLowerCase('pl-PL');

    if (!search) {
      this.items = [...this.allItems];
      return;
    }

    this.items = this.allItems.filter(
      (category: DobryAgentCategory) => {
        const name = (category.name ?? '')
          .trim()
          .toLocaleLowerCase('pl-PL');

        return name.includes(search);
      }
    );
  }

  private emitSelectedItems(): void {
    if (this.multiple) {
      const selected = this.allItems.filter(
        (category: DobryAgentCategory) =>
          this.control.value?.includes(category.uuid)
      );

      this.selectedCategories.emit(selected);
      return;
    }

    const selected = this.allItems.find(
      (category: DobryAgentCategory) =>
        category.uuid === this.control.value
    );

    if (selected) {
      this.selectedCategory.emit(selected);
    }
  }
}
